import { useFeatureFlagVariantKey, usePostHog } from "posthog-js/react"
import { useCallback, useContext, useEffect, useState } from "react"
import { GlobalStateContext } from "../components/providers/GlobalStateProvider.jsx"

// PostHog multivariate feature flag key.
// Dashboard variants: control | test
export const CHEAPER_ADDITIONAL_STORE_PRICING_FLAG =
	"cheaper-prices-for-yearly-and-quarterly-additional-stores"

// Maps the PostHog flag variant to a pricing key in
// kopy_basic.additional_store_experiment. control keeps the pinned 9.99
// winner from the previous experiment; test gets cheaper quarterly/yearly
// prices. Unknown/unset variants fall back to control.
export const resolveAdditionalStorePricingVariant = (flagVariant) => {
	return flagVariant === "test" ? "cheaper_yearly_quarterly" : "price_9_99"
}

export const useFeatureFlagsReady = () => {
	const posthog = usePostHog()
	const [ready, setReady] = useState(
		!posthog?.__loaded || posthog?.featureFlags?.hasLoadedFlags || false,
	)

	useEffect(() => {
		if (!posthog?.__loaded) {
			setReady(true)
			return
		}

		if (posthog.featureFlags?.hasLoadedFlags) {
			setReady(true)
			return
		}

		return posthog.onFeatureFlags(() => {
			setReady(true)
		})
	}, [posthog])

	return ready
}

// Resolves the additional-store pricing variant, or null when it does not
// apply. Only targets additional Kopy Basic stores on the no-trial
// discounted tier. control keeps the pinned 9.99 winner from the previous
// experiment; test gets cheaper quarterly/yearly prices.
export const useAdditionalStorePricingVariant = () => {
	const { state } = useContext(GlobalStateContext)
	const flagVariant = useFeatureFlagVariantKey(
		CHEAPER_ADDITIONAL_STORE_PRICING_FLAG,
	)
	const featureFlagsReady = useFeatureFlagsReady()

	const hasAnotherStoreWithKopyBasic =
		state.otherKopyBasicSubscriptions.length > 0
	const hasTrialedKopyBasicOrProtectBefore =
		state.hasTrialedKopyBasicBefore || state.hasTrialedKopyProtectBefore
	const hasRightToTrial =
		!hasTrialedKopyBasicOrProtectBefore ||
		state.hasNotTrialedFromEmailOfferAndUrlIsFromEmailOffer

	if (!hasAnotherStoreWithKopyBasic || hasRightToTrial) return null
	if (!featureFlagsReady) return undefined

	return resolveAdditionalStorePricingVariant(flagVariant)
}

// Whether the cheaper yearly/quarterly test arm applies (same targeting as
// above), plus the savings percentages to show in the billing period
// selector, computed from the pricing map.
export const useCheaperAdditionalStoresTest = () => {
	const { state } = useContext(GlobalStateContext)
	const pricingVariant = useAdditionalStorePricingVariant()

	const active = pricingVariant === "cheaper_yearly_quarterly"

	const testPricing =
		state.pricing?.["kopy_basic"]?.["additional_store_experiment"]?.[
			"cheaper_yearly_quarterly"
		]

	const monthly = testPricing?.monthly?.priceInUsd
	const quarterly = testPricing?.quarterly?.priceInUsd
	const yearly = testPricing?.yearly?.priceInUsd

	const savings =
		monthly && quarterly && yearly
			? {
					quarterly: Math.round(
						(1 - (quarterly * 4) / (monthly * 12)) * 100,
					),
					yearly: Math.round((1 - yearly / (monthly * 12)) * 100),
				}
			: { quarterly: 20, yearly: 50 }

	return { active, savings }
}

export const useCalculateItemCost = () => {
	const { state } = useContext(GlobalStateContext)
	const additionalStoreVariant = useAdditionalStorePricingVariant()

	const hasAnotherStoreWithKopyBasic =
		state.otherKopyBasicSubscriptions.length > 0
	const hasTrialedKopyBasicOrProtectBefore =
		state.hasTrialedKopyBasicBefore || state.hasTrialedKopyProtectBefore
	const hasRightToTrial =
		!hasTrialedKopyBasicOrProtectBefore ||
		state.hasNotTrialedFromEmailOfferAndUrlIsFromEmailOffer

	return useCallback(
		(item, billingPeriod, returnInFloat = false) => {
			let cost = 0

			const additionalStorePrice =
				state.pricing?.["kopy_basic"]?.[
					"additional_store_experiment"
				]?.[additionalStoreVariant]?.[billingPeriod]?.priceInUsd

			if (
				item === "basic" &&
				additionalStoreVariant &&
				typeof additionalStorePrice === "number"
			) {
				cost = additionalStorePrice
			} else if (item === "basic") {
				if (billingPeriod === "monthly") {
					cost =
						state.pricing?.["kopy_basic"]?.["monthly"]?.[
							hasAnotherStoreWithKopyBasic ? "discounted" : "base"
						]?.[hasRightToTrial ? "with_trial" : "no_trial"]
							?.priceInUsd || 0
				} else if (billingPeriod === "yearly") {
					cost =
						state.pricing?.["kopy_basic"]?.["yearly"]?.[
							hasAnotherStoreWithKopyBasic ? "discounted" : "base"
						]?.[hasRightToTrial ? "with_trial" : "no_trial"]
							?.priceInUsd || 0
				} else if (billingPeriod === "quarterly") {
					cost =
						state.pricing?.["kopy_basic"]?.["quarterly"]?.[
							hasAnotherStoreWithKopyBasic ? "discounted" : "base"
						]?.[hasRightToTrial ? "with_trial" : "no_trial"]
							?.priceInUsd || 0
				}
			} else if (item === "plus") {
				cost =
					state.pricing?.["kopy_plus"]?.[billingPeriod]?.[
						hasRightToTrial ? "with_trial" : "no_trial"
					]?.priceInUsd || 0
			} else if (item === "protect") {
				cost =
					state.pricing?.["kopy_protect"]?.[billingPeriod]?.[
						hasRightToTrial ? "with_trial" : "no_trial"
					]?.priceInUsd || 0
			}

			if (returnInFloat) {
				return cost
			} else {
				return new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: "USD",
				}).format(cost)
			}
		},
		[
			additionalStoreVariant,
			hasAnotherStoreWithKopyBasic,
			hasRightToTrial,
			state.pricing,
		],
	)
}

export const useGetItemPriceId = () => {
	const { state } = useContext(GlobalStateContext)
	const additionalStoreVariant = useAdditionalStorePricingVariant()

	const hasAnotherStoreWithKopyBasic =
		state.otherKopyBasicSubscriptions.length > 0
	const hasTrialedKopyBasicOrProtectBefore =
		state.hasTrialedKopyBasicBefore || state.hasTrialedKopyProtectBefore
	const hasRightToTrial =
		!hasTrialedKopyBasicOrProtectBefore ||
		state.hasNotTrialedFromEmailOfferAndUrlIsFromEmailOffer

	return (item, billingPeriod) => {
		let priceId = null

		const additionalStorePriceId =
			state.pricing?.["kopy_basic"]?.["additional_store_experiment"]?.[
				additionalStoreVariant
			]?.[billingPeriod]?.priceId

		if (
			item === "basic" &&
			additionalStoreVariant &&
			additionalStorePriceId
		) {
			priceId = additionalStorePriceId
		} else if (item === "basic") {
			if (billingPeriod === "monthly") {
				priceId =
					state.pricing?.["kopy_basic"]?.["monthly"]?.[
						hasAnotherStoreWithKopyBasic ? "discounted" : "base"
					]?.[hasRightToTrial ? "with_trial" : "no_trial"]?.priceId ||
					null
			} else if (billingPeriod === "yearly") {
				priceId =
					state.pricing?.["kopy_basic"]?.["yearly"]?.[
						hasAnotherStoreWithKopyBasic ? "discounted" : "base"
					]?.[hasRightToTrial ? "with_trial" : "no_trial"]?.priceId ||
					null
			} else if (billingPeriod === "quarterly") {
				priceId =
					state.pricing?.["kopy_basic"]?.["quarterly"]?.[
						hasAnotherStoreWithKopyBasic ? "discounted" : "base"
					]?.[hasRightToTrial ? "with_trial" : "no_trial"]?.priceId ||
					null
			}
		} else if (item === "plus") {
			if (billingPeriod === "monthly") {
				priceId =
					state.pricing?.["kopy_plus"]?.["monthly"]?.[
						hasRightToTrial ? "with_trial" : "no_trial"
					]?.priceId || null
			} else if (billingPeriod === "yearly") {
				priceId =
					state.pricing?.["kopy_plus"]?.["yearly"]?.[
						hasRightToTrial ? "with_trial" : "no_trial"
					]?.priceId || null
			} else if (billingPeriod === "quarterly") {
				priceId =
					state.pricing?.["kopy_plus"]?.["quarterly"]?.[
						hasRightToTrial ? "with_trial" : "no_trial"
					]?.priceId || null
			}
		} else if (item === "protect") {
			if (billingPeriod === "monthly") {
				priceId =
					state.pricing?.["kopy_protect"]?.["monthly"]?.[
						hasRightToTrial ? "with_trial" : "no_trial"
					]?.priceId || null
			} else if (billingPeriod === "yearly") {
				priceId =
					state.pricing?.["kopy_protect"]?.["yearly"]?.[
						hasRightToTrial ? "with_trial" : "no_trial"
					]?.priceId || null
			} else if (billingPeriod === "quarterly") {
				priceId =
					state.pricing?.["kopy_protect"]?.["quarterly"]?.[
						hasRightToTrial ? "with_trial" : "no_trial"
					]?.priceId || null
			}
		}

		return priceId
	}
}
